// Wording of the Email Inbox (Arabic / English). The page decides nothing: it shows
// what the server returns. "Sent" always means "accepted by the mail provider", never
// "delivered" - the notes below say so wherever a status is explained.

export const MAIL_TEXT = {
  en: {
    title: "Email Inbox",
    subtitle: "Every email the platform sent: who it went to, what it said and what the mail provider answered.",
    refresh: "Refresh",
    cards: { total: "Total", sent: "Sent", failed: "Failed", pending: "Pending" },
    search: "Search subject, recipient, sender, store or request",
    filters: { status: "Status", type: "Type", source: "Source", store: "Store", app: "App", from: "From date", to: "To date", sort: "Sort by", order: "Order", pageSize: "Per page", all: "All", reset: "Reset filters" },
    sorts: { date: "Date", status: "Status", subject: "Subject" },
    orders: { desc: "Newest first", asc: "Oldest first" },
    status: { pending: "Pending", sending: "Sending", sent: "Sent", failed: "Failed", uncertain: "Unknown outcome", skipped: "Not sent (mail disabled)" },
    statusHelp: {
      sent: "The mail provider accepted this email. That does not confirm it reached or was read by the recipient.",
      failed: "The mail provider (or our configuration) refused this email. When it is safe, it is retried automatically; see the retry details below.",
      uncertain: "The connection was lost or the provider had an error, so it is not known whether the email was accepted. It is never re-sent automatically.",
      pending: "The email is being sent.",
      skipped: "Email sending is disabled on the server, so nothing was sent.",
    },
    columns: { subject: "Subject", to: "Recipients", type: "Type", store: "Store", status: "Status", date: "Date" },
    more: (n) => `+${n} more`,
    loading: "Loading emails…",
    emptyTitle: "No emails yet",
    emptyText: "Emails sent by the platform will appear here.",
    noMatchTitle: "No emails match these filters",
    noMatchText: "Try changing the search or clearing the filters.",
    errorTitle: "The email history could not be loaded",
    errorText: "Check your connection and try again.",
    retryLoad: "Try again",
    pagination: { nav: "Email pages", previous: "Previous", next: "Next", summary: (page, pages, total) => `Page ${page}${pages ? ` of ${pages}` : ""}${total != null ? ` · ${total} emails` : ""}`, limit: "" },
    detail: {
      close: "Close",
      loadFailed: "The email could not be loaded.",
      from: "From",
      to: "To",
      cc: "Cc",
      bcc: "Bcc",
      subject: "Subject",
      body: "Message",
      attachments: "Attachments",
      type: "Type",
      source: "Source",
      store: "Store",
      app: "App",
      request: "Related request",
      initiatedBy: "Started by",
      system: "System",
      created: "Recorded",
      sentAt: "Accepted by provider",
      updated: "Updated",
      providerId: "Provider message id",
      providerStatus: "Provider status",
      delivery: "Delivery",
      deliveryUnknown: "Not confirmed (acceptance only)",
      error: "Last error",
      attempts: "Delivery attempts",
      attempt: "Attempt",
      results: { sending: "In progress", accepted: "Accepted by provider", failed: "Refused", uncertain: "Unknown outcome", skipped: "Not sent" },
      bodyWithheld: "The body of this email is not stored because it contains a login credential.",
      bodyTruncated: "The stored body was shortened because it is very large.",
      noBody: "No message body was recorded.",
      attachmentGone: "Not retained after sending",
      download: "Download",
      downloadFailed: "The attachment could not be downloaded.",
      retry: "Send again",
      retryFlow: "This email is retried from the place that created it, which applies its own rules.",
      retryUnavailable: "This email cannot be re-sent from here.",
      attemptsUsed: "Attempts",
      lastAttempt: "Last attempt",
      nextRetry: "Next automatic retry",
      retryOwner: "Retried by",
      owners: { queue: "Automatic retry queue", flow: "The flow that created it", manual: "An administrator (nothing automatic)", none: "Nothing to retry" },
      reasons: {
        SCHEDULED: "An automatic retry is scheduled.",
        FLOW_OWNED: "The flow that created it retries it.",
        UNCERTAIN: "The outcome is unknown, so it is never re-sent automatically. Confirm before sending it again.",
        PERMANENT_FAILURE: "Refused for good: retrying will not help until the cause is fixed.",
        MAX_ATTEMPTS: "Automatic retries are used up.",
        CREDENTIAL_BODY_WITHHELD: "It contains a login credential, so it is never re-sent from history. Repeat the action that created it.",
        ATTACHMENTS_NOT_RETAINED: "Its attachment is not kept after sending, so it cannot be re-sent from history. Repeat the action that created it.",
        BODY_NOT_RETAINED: "Its body was not fully kept, so it cannot be re-sent from history.",
        SKIPPED_DISABLED: "Sending was switched off. It can be sent once email is enabled.",
        AUTO_RETRY_OFF: "Automatic retry is switched off on the server.",
      },
      deliveryStatuses: { unknown: "Not confirmed (acceptance only)", deferred: "Deferred by the receiving server", delivered: "Delivered", bounced: "Bounced", dropped: "Dropped by the provider", spam: "Reported as spam", unsubscribed: "Unsubscribed" },
      autoAttempt: "Automatic retry",
      acknowledgedAttempt: "Sent again knowing it may be a duplicate",
      acknowledge: "I understand this email may already have been delivered and that sending it again may create a duplicate.",
    },
    retryDialog: {
      title: "Send this email again?",
      description: "The same message will be sent again to the same recipients and recorded as a new attempt on this email.",
      confirm: "Send again",
      cancel: "Cancel",
      failed: "The email could not be sent again.",
      stillFailed: "The mail provider did not accept the email again. The attempt was recorded.",
      done: "The mail provider accepted the email.",
    },
  },
  ar: {
    title: "صندوق البريد",
    subtitle: "كل رسالة بريد أرسلتها المنصة: لمن أُرسلت وماذا تضمنت وبماذا ردّ مزوّد البريد.",
    refresh: "تحديث",
    cards: { total: "الإجمالي", sent: "تم الإرسال", failed: "فشل", pending: "قيد الإرسال" },
    search: "ابحث بالموضوع أو المستلم أو المرسل أو المتجر أو الطلب",
    filters: { status: "الحالة", type: "النوع", source: "المصدر", store: "المتجر", app: "التطبيق", from: "من تاريخ", to: "إلى تاريخ", sort: "ترتيب حسب", order: "الاتجاه", pageSize: "في الصفحة", all: "الكل", reset: "إعادة ضبط الفلاتر" },
    sorts: { date: "التاريخ", status: "الحالة", subject: "الموضوع" },
    orders: { desc: "الأحدث أولاً", asc: "الأقدم أولاً" },
    status: { pending: "قيد الانتظار", sending: "قيد الإرسال", sent: "تم الإرسال", failed: "فشل", uncertain: "النتيجة غير معروفة", skipped: "لم يُرسل (البريد معطّل)" },
    statusHelp: {
      sent: "قبِل مزوّد البريد الرسالة. هذا لا يؤكد وصولها للمستلم أو قراءتها.",
      failed: "رفض مزوّد البريد (أو إعداداتنا) هذه الرسالة.",
      uncertain: "انقطع الاتصال أو حدث خطأ لدى المزوّد، فلا يُعرف إن قُبلت الرسالة. لا تُعاد تلقائياً.",
      pending: "الرسالة قيد الإرسال.",
      skipped: "إرسال البريد معطّل على الخادم، لذلك لم يُرسل شيء.",
    },
    columns: { subject: "الموضوع", to: "المستلمون", type: "النوع", store: "المتجر", status: "الحالة", date: "التاريخ" },
    more: (n) => `+${n} أخرى`,
    loading: "جارٍ تحميل الرسائل…",
    emptyTitle: "لا توجد رسائل بعد",
    emptyText: "ستظهر هنا الرسائل التي ترسلها المنصة.",
    noMatchTitle: "لا توجد رسائل مطابقة",
    noMatchText: "جرّب تغيير البحث أو مسح الفلاتر.",
    errorTitle: "تعذّر تحميل سجل البريد",
    errorText: "تحقق من اتصالك وحاول مرة أخرى.",
    retryLoad: "إعادة المحاولة",
    pagination: { nav: "صفحات الرسائل", previous: "السابق", next: "التالي", summary: (page, pages, total) => `الصفحة ${page}${pages ? ` من ${pages}` : ""}${total != null ? ` · ${total} رسالة` : ""}`, limit: "" },
    detail: {
      close: "إغلاق",
      loadFailed: "تعذّر تحميل الرسالة.",
      from: "من",
      to: "إلى",
      cc: "نسخة (Cc)",
      bcc: "نسخة مخفية (Bcc)",
      subject: "الموضوع",
      body: "نص الرسالة",
      attachments: "المرفقات",
      type: "النوع",
      source: "المصدر",
      store: "المتجر",
      app: "التطبيق",
      request: "الطلب المرتبط",
      initiatedBy: "بدأها",
      system: "النظام",
      created: "تاريخ التسجيل",
      sentAt: "قبول المزوّد",
      updated: "آخر تحديث",
      providerId: "معرّف الرسالة لدى المزوّد",
      providerStatus: "رمز المزوّد",
      delivery: "التسليم",
      deliveryUnknown: "غير مؤكد (قبول فقط)",
      error: "آخر خطأ",
      attempts: "محاولات الإرسال",
      attempt: "المحاولة",
      results: { sending: "قيد التنفيذ", accepted: "قبِلها المزوّد", failed: "رفضها المزوّد", uncertain: "النتيجة غير معروفة", skipped: "لم تُرسل" },
      bodyWithheld: "لا يُحفظ نص هذه الرسالة لأنها تحتوي على بيانات دخول.",
      bodyTruncated: "تم اختصار النص المحفوظ لأنه كبير جداً.",
      noBody: "لم يُسجَّل نص للرسالة.",
      attachmentGone: "لم يُحتفظ به بعد الإرسال",
      download: "تنزيل",
      downloadFailed: "تعذّر تنزيل المرفق.",
      retry: "إعادة الإرسال",
      retryFlow: "تُعاد هذه الرسالة من المكان الذي أنشأها، وفق قواعده الخاصة.",
      retryUnavailable: "لا يمكن إعادة إرسال هذه الرسالة من هنا.",
      attemptsUsed: "المحاولات",
      lastAttempt: "آخر محاولة",
      nextRetry: "إعادة المحاولة التلقائية القادمة",
      retryOwner: "تتم إعادتها بواسطة",
      owners: { queue: "طابور الإعادة التلقائية", flow: "المسار الذي أنشأها", manual: "مسؤول (لا شيء تلقائي)", none: "لا شيء لإعادته" },
      reasons: {
        SCHEDULED: "تمت جدولة إعادة محاولة تلقائية.",
        FLOW_OWNED: "يعيد المسار الذي أنشأها المحاولة.",
        UNCERTAIN: "النتيجة غير معروفة، لذلك لا تُعاد تلقائياً. أكّد قبل إرسالها مرة أخرى.",
        PERMANENT_FAILURE: "رُفضت نهائياً: لن تنفع الإعادة قبل معالجة السبب.",
        MAX_ATTEMPTS: "استُنفدت محاولات الإعادة التلقائية.",
        CREDENTIAL_BODY_WITHHELD: "تحتوي على بيانات دخول، لذلك لا تُعاد من السجل. كرّر الإجراء الذي أنشأها.",
        ATTACHMENTS_NOT_RETAINED: "لا يُحتفظ بمرفقها بعد الإرسال، فلا يمكن إعادتها من السجل. كرّر الإجراء الذي أنشأها.",
        BODY_NOT_RETAINED: "لم يُحتفظ بنصها كاملاً، فلا يمكن إعادتها من السجل.",
        SKIPPED_DISABLED: "كان الإرسال متوقفاً. يمكن إرسالها بعد تفعيل البريد.",
        AUTO_RETRY_OFF: "الإعادة التلقائية متوقفة على الخادم.",
      },
      deliveryStatuses: { unknown: "غير مؤكد (القبول فقط)", deferred: "مؤجلة لدى الخادم المستقبل", delivered: "تم التسليم", bounced: "ارتدّت", dropped: "أسقطها المزوّد", spam: "أُبلغ عنها كرسالة مزعجة", unsubscribed: "ألغى الاشتراك" },
      autoAttempt: "إعادة تلقائية",
      acknowledgedAttempt: "أُعيد إرسالها مع العلم بإمكانية التكرار",
      acknowledge: "أدرك أن هذه الرسالة قد تكون وصلت فعلاً وأن إرسالها مرة أخرى قد يسبب تكراراً.",
    },
    retryDialog: {
      title: "إعادة إرسال هذه الرسالة؟",
      description: "ستُرسل الرسالة نفسها مرة أخرى إلى المستلمين أنفسهم وتُسجَّل كمحاولة جديدة.",
      confirm: "إعادة الإرسال",
      cancel: "إلغاء",
      failed: "تعذّرت إعادة إرسال الرسالة.",
      stillFailed: "لم يقبل مزوّد البريد الرسالة مجدداً. تم تسجيل المحاولة.",
      done: "قبِل مزوّد البريد الرسالة.",
    },
  },
};

// Known email types and sources; anything else is shown as its raw key.
export const TYPE_LABELS = {
  en: {
    "auth.passwordReset": "Password reset code",
    "crm.newClientInvitation": "New client invitation",
    "notification.copy": "Notification copy",
    "support.ticketCreated": "Support ticket created",
    "support.ticketUpdated": "Support ticket updated",
    "support.ticketAssigned": "Support ticket assigned",
    "support.ticketClosed": "Support ticket closed",
    "integration.requestNotice": "LUD Connect request",
    "onboarding.firstInvoiceReady": "Onboarding: invoice ready",
    "onboarding.paymentReceiptReceived": "Onboarding: receipt received",
    "onboarding.paymentApproved": "Onboarding: payment approved",
    "onboarding.paymentRejected": "Onboarding: payment rejected",
    "onboarding.menuTeamReady": "Onboarding: menu team",
    "objection.compensationClaim": "Objection",
    "dispute.deliveryApp": "Delivery dispute",
    "applications.subscription": "App registration",
    "menu.sendToApp": "Menu to app",
    "menu.changeNotice": "Menu change notice",
    "menu.bulkChangeNotice": "Menu bulk change notice",
    "requests.menu.appEmail": "Request: menu to app",
    "requests.branchChange.appNotice": "Request: branch change notice",
    uncategorized: "Uncategorized",
  },
  ar: {
    "auth.passwordReset": "رمز استعادة كلمة المرور",
    "crm.newClientInvitation": "دعوة عميل جديد",
    "notification.copy": "نسخة من إشعار",
    "support.ticketCreated": "إنشاء تذكرة دعم",
    "support.ticketUpdated": "تحديث تذكرة دعم",
    "support.ticketAssigned": "إسناد تذكرة دعم",
    "support.ticketClosed": "إغلاق تذكرة دعم",
    "integration.requestNotice": "طلب LUD Connect",
    "onboarding.firstInvoiceReady": "التهيئة: الفاتورة جاهزة",
    "onboarding.paymentReceiptReceived": "التهيئة: استلام الإيصال",
    "onboarding.paymentApproved": "التهيئة: اعتماد الدفع",
    "onboarding.paymentRejected": "التهيئة: رفض الدفع",
    "onboarding.menuTeamReady": "التهيئة: فريق القائمة",
    "objection.compensationClaim": "اعتراض",
    "dispute.deliveryApp": "نزاع مع تطبيق التوصيل",
    "applications.subscription": "تسجيل في تطبيق",
    "menu.sendToApp": "إرسال القائمة للتطبيق",
    "menu.changeNotice": "إشعار تعديل القائمة",
    "menu.bulkChangeNotice": "إشعار تعديل جماعي للقائمة",
    "requests.menu.appEmail": "طلب: القائمة للتطبيق",
    "requests.branchChange.appNotice": "طلب: إشعار تغيير الفرع",
    uncategorized: "غير مصنّف",
  },
};

export const SOURCE_LABELS = {
  en: { auth: "Sign-in", crm: "CRM", notifications: "Notifications", support: "Support", integration: "LUD Connect", onboarding: "Onboarding", objections: "Objections", disputes: "Disputes", applications: "Applications", menu: "Menu", requests: "Requests", billing: "Billing", copilot: "Copilot", dashboard: "Dashboard", other: "Other" },
  ar: { auth: "تسجيل الدخول", crm: "إدارة العملاء", notifications: "الإشعارات", support: "الدعم", integration: "LUD Connect", onboarding: "التهيئة", objections: "الاعتراضات", disputes: "النزاعات", applications: "التطبيقات", menu: "القائمة", requests: "الطلبات", billing: "الفوترة", copilot: "المساعد", dashboard: "لوحة التحكم", other: "أخرى" },
};

export const formatDateTime = (value, lang) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";
};

export const labelFor = (map, lang, key) => (map[lang === "ar" ? "ar" : "en"] || map.en)[key] || key;
